import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
	ModalReminderData,
	ModalReminderFormProps,
	ModalReminderMode,
} from "../../models/components/modal-reminder.model";
import UiButton from "../button/Button";
import UiFieldMessage from "../field-message/FieldMessage.component";
import { Form } from "react-final-form";
import FormSection from "../form-section/FormSection.component";
import UiInputDate from "../input-date/InputDate.component";
import UiInputText from "../input-text/InputText.component";
import { Validations } from "../../services/form/validations.service";
import { Formatter } from "../../services/formatter/formatter.service";
import { EndpointsService } from "../../services/endpoints/endpoints.service";
import { IReminder } from "../../models/entities/reminder.model";
import { AxiosError } from "axios";
import { mappedAxiosError } from "../../services/http/http.service";
import { getSessionStorageUser } from "../../services/session/auth.service";
import { FormApi } from "final-form";
import UiSelect from "../select/Select.component";
import { ListOption } from "../../models/misc.model";
import { useActiveProvidersInfo } from "../../hooks/useActiveProvidersInfo";
import UiSelectButton from "../select-button/SelectButton.component";

const ModalReminderForm = (props: ModalReminderFormProps) => {
	const { t } = useTranslation("common");

	const initialFormData: ModalReminderData = {
		mode: props.mode,
		kannactId: props.entity?.kannactId ?? null,
		patientId: props.entity?.patientId ?? null,
		reason: props.entity?.reason ?? null,
		dateOn: props.entity?.dateOn ? Formatter.dateISOToUTC(props.entity?.dateOn) : null,
		formMessage: undefined,
		assignedTo: props.entity?.assignedTo ?? getSessionStorageUser().id!,
		snoozeValue: 0,
	};

	const [formData, setFormData] = useState<ModalReminderData>({ ...initialFormData });
	const formApiRef = useRef<FormApi<ModalReminderData, Partial<ModalReminderData>> | null>(null);

	// Sync formData.dateOn with form field when it changes (for snooze options)
	useEffect(() => {
		if (formApiRef.current && formData.dateOn) {
			const currentFieldValue = formApiRef.current.getFieldState("dateOn")?.value;
			if (currentFieldValue?.getTime() !== formData.dateOn.getTime()) {
				formApiRef.current.change("dateOn", formData.dateOn);
			}
		}
	}, [formData.dateOn]);

	const snoozeOptions = [
		{ id: 1, label: t("UI_COMPONENTS.MODAL_REMINDER.FORM_FIELDS.ONEDAY") },
		{ id: 7, label: t("UI_COMPONENTS.MODAL_REMINDER.FORM_FIELDS.ONEWEEK") },
		{ id: 30, label: t("UI_COMPONENTS.MODAL_REMINDER.FORM_FIELDS.ONEMONTH") },
		{ id: 90, label: t("UI_COMPONENTS.MODAL_REMINDER.FORM_FIELDS.THREEMONTHS") },
		{ id: 365, label: t("UI_COMPONENTS.MODAL_REMINDER.FORM_FIELDS.ONEYEAR") },
		{ id: 0, label: t("UI_COMPONENTS.MODAL_REMINDER.FORM_FIELDS.CUSTOM") },
	];

	const coaches = useActiveProvidersInfo();

	// Submit
	const handleSubmit = async (
		data: ModalReminderData,
		form: FormApi<ModalReminderData, Partial<ModalReminderData>>
	) => {
		// Reset login error
		setFormData({ ...data, formMessage: undefined });

		if (formData.mode === ModalReminderMode.CREATE) {
			// Create
			create(data);
		} else {
			// Update / Snooze
			update(data);
		}
	};

	/* CREATE */

	const create = async (data: ModalReminderData): Promise<void> => {
		const now = new Date();

		await EndpointsService.dataRetriever
			.createReminder({
				body: {
					id: null,
					kannactId: data.kannactId as string,
					patientId: Number(data.kannactId?.substring(3)),
					reason: data.reason as string,
					dateOn: Formatter.dateUTCWithoutTime(data.dateOn)?.toISOString() as string,
					resolved: false,
					createdOn: now.toISOString(),
					updatedOn: now.toISOString(),
					createdBy: getSessionStorageUser().id!,
					assignedTo: data.assignedTo as number,
				},
			})
			.then((response: IReminder) => props.onComplete(true, response))
			.catch((error: AxiosError) => {
				// Error message
				data.formMessage = mappedAxiosError(
					undefined,
					"UI_COMPONENTS.MODAL_REMINDER.MESSAGE_ERROR"
				);
				setFormData(data);
			});
	};

	const calculateSnoozeDate = (e: any) => {
		let nextDate = initialFormData.dateOn;
		const dayToMilliseconds = 24 * 60 * 60 * 1000; // Number of milliseconds in a day
		if (e.value !== 0 && formData.dateOn) {
			nextDate = new Date(
				initialFormData.dateOn!.getTime() + e.target.value * dayToMilliseconds
			); // Add snooze days as milliseconds
			// Adjust to next working day if weekend
			nextDate = Formatter.getNextWorkingDate(nextDate);
		}

		setFormData({
			...formData,
			dateOn: nextDate,
			snoozeValue: e.target.value,
		});
	};

	/* UPDATE / SNOOZE */

	const update = async (data: ModalReminderData): Promise<void> => {
		await EndpointsService.dataRetriever
			.updateReminder({
				body: {
					id: props.entity?.id as number,
					kannactId: data.kannactId as string,
					patientId: Number(data.kannactId?.substring(3)),
					reason: data.reason as string,
					dateOn: Formatter.dateUTCWithoutTime(data.dateOn)?.toISOString() as string,
					resolved: false,
					createdOn: props.entity?.createdOn as string,
					assignedTo: data.assignedTo as number,
					updatedOn: new Date().toISOString(),
				},
			})
			.then((response: IReminder) => props.onComplete(true, response))
			.catch((error: AxiosError) => {
				// Error message
				data.formMessage = mappedAxiosError(
					undefined,
					"UI_COMPONENTS.MODAL_REMINDER.MESSAGE_ERROR"
				);
				setFormData(data);
			});
	};

	return (
		<Form
			onSubmit={handleSubmit}
			initialValues={formData}
			render={({ handleSubmit, form, submitting }) => {
				// Store form API ref for useEffect
				formApiRef.current = form;
				return (
					<form onSubmit={handleSubmit}>
						<p className="mb-4">{t("UI_COMPONENTS.MODAL_REMINDER.MSG_INFO")}</p>

						<FormSection className="row">
							<div className="row">
								{/* Kannact ID */}
								<div className="col-12 col-sm-6 col-md-6 col-lg-6">
									<UiInputText
										id="kannactId"
										label="UI_COMPONENTS.MODAL_REMINDER.FORM_FIELDS.kannactId"
										name="kannactId"
										onChange={(e) =>
											setFormData({ ...formData, kannactId: e.target.value })
										}
										validations={[Validations.required]}
										disabled={submitting}
									/>
								</div>

								{/* Assigned To */}
								<div className="col-12 col-sm-6 col-md-6 col-lg-6">
									<UiSelect
										id="assignedTo"
										label={
											!formData.assignedTo &&
											(coaches.isFetching || coaches.isLoading)
												? "UI_COMPONENTS.SELECT.LOADING"
												: "UI_COMPONENTS.MODAL_REMINDER.FORM_FIELDS.assignedTo"
										}
										name="assignedTo"
										onChange={(e) =>
											setFormData({ ...formData, assignedTo: e.target.value })
										}
										options={
											coaches.data?.map(
												(item) =>
													new ListOption({
														id: item.coachId,
														label: Formatter.fullName(
															item.firstName,
															item.lastName
														),
													})
											) ?? []
										}
										disabled={submitting}
										filter={true}
									/>
								</div>
							</div>
							{/* Reason */}
							<div className="col-12 col-sm-12 col-md-12 col-lg-12">
								<UiInputText
									id="reason"
									label="UI_COMPONENTS.MODAL_REMINDER.FORM_FIELDS.reason"
									name="reason"
									onChange={(e) =>
										setFormData({ ...formData, reason: e.target.value })
									}
									validations={[Validations.required]}
									disabled={submitting}
								/>
							</div>

							{/* Date */}
							<div className="col-12 col-sm-12 col-md-12 col-lg-8">
								<div className="participant-modal-reminders__date-input-container">
									<UiInputDate
										id="dateOn"
										name="dateOn"
										label="UI_COMPONENTS.MODAL_REMINDER.FORM_FIELDS.dateOn"
										onChange={(e) => {
											const selectedDate = e.target.value as Date;
											// Don't adjust weekend dates when manually selected
											setFormData({
												...formData,
												dateOn: selectedDate,
												snoozeValue: 0,
											});
										}}
										minDate={new Date()}
										validations={[Validations.required]}
										disabled={submitting}
										inline={true}
									/>
									{props.mode === ModalReminderMode.UPDATE && (
										<div className="col-12 col-sm-12 col-md-12 col-lg-6">
											<p className="secondary-title">
												{t(
													"UI_COMPONENTS.MODAL_REMINDER.FORM_FIELDS.SNOOZE"
												)}
											</p>
											<UiSelectButton
												id="snoozeValue"
												className="vertical"
												name="snoozeValue"
												onChange={calculateSnoozeDate}
												options={snoozeOptions}
											/>
										</div>
									)}
								</div>
							</div>
						</FormSection>

						{formData.formMessage && (
							<div className="form-message">
								<UiFieldMessage {...formData.formMessage}></UiFieldMessage>
							</div>
						)}

						<div className="action-buttons">
							<UiButton
								label="UI_COMPONENTS.BUTTONS.CANCEL"
								className="p-button-outlined p-button-rounded"
								type="button"
								onClick={props.onCancel}
								disabled={submitting}
							/>
							<UiButton
								label="UI_COMPONENTS.BUTTONS.CONFIRM"
								className="p-button-rounded"
								type="submit"
								loading={submitting}
								disabled={submitting}
							/>
						</div>
					</form>
				);
			}}
		/>
	);
};
export default ModalReminderForm;
